import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';

/* ════════════════════════════════════════════════════════════════════════
 * FEATURES — new variants (icon-row · integration-cards · phone-mockup ·
 * laptop-mockup · six-icon-violet)
 * ════════════════════════════════════════════════════════════════════════ */
export function FeaturesExt({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const layout = block.layout;
  const items = c.items || Array.from({length:6}).map((_,i)=>({ title:`Feature ${i+1}`, description:'Short description' }));

  if (layout === 'icon-row') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-16" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto text-center">
          <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold mb-12" style={{ fontFamily: theme.headingFont }}>{c.title || 'Our Benefits'}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {items.slice(0,3).map((it,i)=>(
              <div key={i}>
                <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center text-2xl" style={{ background: theme.accent + '20', color: theme.accent }}>{it.icon || '✦'}</div>
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="mt-4 font-bold" placeholder='Title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-1 text-sm opacity-70" placeholder='Description' />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'integration-cards') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }}>{c.title || 'Create Something Awesome'}</h2>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-3 opacity-70 max-w-xl mx-auto" placeholder='Tagline (optional)' />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {items.slice(0,2).map((it,i)=>(
              <div key={i} className="border border-gray-200 rounded-2xl p-8 text-center" style={{ borderRadius: theme.radius || 16 }}>
                <div className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center text-2xl" style={{ background: theme.accent + '15', color: theme.accent }}>{it.icon || '⬆'}</div>
                <p className="mt-4 font-bold text-lg">{it.title || `Connect with ${i===0?'Dropbox':'GitHub'}`}</p>
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-2 text-sm opacity-70" placeholder='Description' />
                <button className="mt-6 px-6 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider text-white" style={{ background: theme.accent || '#2DD4BF' }}>Get Started</button>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'phone-mockup') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Take a Look at Our Awesome New Product'}</H1>
            <div className="mt-6 space-y-4">
              {items.slice(0,3).map((it,i)=>(
                <div key={i}><E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="font-bold text-sm" placeholder='Title' /><E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="text-sm opacity-70" placeholder='Description' /></div>
              ))}
            </div>
          </div>
          <div className="relative h-96 flex justify-center">
            {c.image_url ? <SafeImg src={c.image_url} alt="" className="h-full object-contain" /> : <div className="w-48 h-96 bg-gradient-to-br from-gray-800 to-black rounded-3xl shadow-2xl" />}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'laptop-mockup') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow block={block} onEdit={onEdit} field='eyebrow' theme={theme}  testid={tid('eyebrow')}>{c.eyebrow}</Eyebrow>
            <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Designed for Greatness'}</H1>
            <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 opacity-75" placeholder='Tagline (optional)' />
            <div className="mt-6 space-y-3">
              {items.slice(0,2).map((it,i)=>(
                <div key={i} className="flex gap-3"><span className="text-violet-500">✓</span><div><E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="font-bold text-sm" placeholder='Title' /><E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="text-sm opacity-70" placeholder='Description' /></div></div>
              ))}
            </div>
          </div>
          <div className="aspect-video bg-gradient-to-br from-gray-800 to-black rounded-xl shadow-2xl overflow-hidden">
            {c.image_url && <SafeImg src={c.image_url} alt="" className="w-full h-full object-cover" />}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'six-icon-violet') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: '#3B1F8B' }}>
        <div className="max-w-5xl mx-auto text-white text-center">
          <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }}>{c.title || 'Spend Less. Free Coding.'}</h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 gap-8 text-left">
            {items.slice(0,6).map((it,i)=>(
              <div key={i}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-lg bg-white/10">{it.icon || '✦'}</div>
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="mt-3 font-bold text-sm" placeholder='Title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-1 text-xs opacity-75" placeholder='Description' />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'two-by-two-cards') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto">
          <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold text-center" style={{ fontFamily: theme.headingFont }}>{c.title || 'Features of Startup Framework'}</h2>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {items.slice(0,4).map((it,i) => (
              <div key={i} className="border border-current/10 rounded-2xl p-6" style={{ borderRadius: theme.radius || 16 }}>
                {it.image ? (
                  <div className="aspect-video rounded-lg overflow-hidden mb-4"><SafeImg src={it.image} alt={it.title} className="w-full h-full object-cover" /></div>
                ) : (
                  <div className="aspect-video rounded-lg mb-4 bg-gradient-to-br from-violet-200 to-fuchsia-300" />
                )}
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="font-bold" placeholder='Title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-1 text-sm opacity-70" placeholder='Description' />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'six-feature-checklist') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-4xl mx-auto">
          <h2 data-testid={tid('title')} className="text-3xl md:text-4xl font-bold" style={{ fontFamily: theme.headingFont }}>{c.title || 'A list of features'}</h2>
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-3 opacity-70" placeholder='Tagline (optional)' />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-4">
            {items.slice(0,6).map((it,i) => (
              <div key={i} className="flex gap-3 items-start py-2 border-b border-current/10">
                <span className="text-lg" style={{ color: theme.accent }}>✓</span>
                <div>
                  <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="font-bold text-sm" placeholder='Title' />
                  <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="text-xs opacity-70 mt-0.5" placeholder='Description' />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (layout === 'three-stat-block') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-5xl mx-auto text-center">
          <H1 block={block} onEdit={onEdit} field='title' theme={theme} size="text-3xl md:text-4xl"  testid={tid('title')}>{c.title || 'Startup Framework'}</H1>
          <div className="mt-12 grid grid-cols-3 gap-8">
            {items.slice(0,3).map((it,i) => (
              <div key={i}>
                <E block={block} onEdit={onEdit} field={`items.${i}.value`} value={it.value || `0${i+1}`} tag='p' className="text-5xl font-bold" style={{ color: theme.accent, fontFamily: theme.headingFont }} placeholder='42' />
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="mt-2 font-bold text-sm" placeholder='Title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-1 text-xs opacity-70" placeholder='Description' />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ════════════════════════════════════════════════════════════════════
   * Iter 178f · Services Section Audit
   * Each layout below renders the actual editor block to match its
   * `CategoryShapes.js` preview tile 1:1. Names mirror the preview keys.
   * ════════════════════════════════════════════════════════════════════ */

  // ── Icon Features · 4-up (matches `ServicesIconFeatures` preview) ──
  if (layout === 'icon-features-4up') {
    const fourItems = items.length ? items : [
      { icon: '✦', title: 'Brand' },
      { icon: '◐', title: 'Strategy' },
      { icon: '◑', title: 'Design' },
      { icon: '✚', title: 'Build' },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          <E block={block} onEdit={onEdit} field='title' value={c.title || 'What we do'} tag='h2' className="text-3xl md:text-4xl font-bold text-center mb-14" style={{ fontFamily: theme.headingFont }} placeholder='Headline' />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            {fourItems.slice(0,4).map((it,i)=>(
              <div key={i} className="text-center">
                {(it.image || it.image_url) ? (
                  <div className="w-24 h-24 mx-auto mb-5 rounded-2xl overflow-hidden" style={{ background: theme.accent + '15' }}>
                    <SafeImg src={it.image || it.image_url} alt={it.title || ''} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-24 h-24 mx-auto mb-5 flex items-center justify-center rounded-2xl text-4xl" style={{ background: theme.accent + '15', color: theme.accent }}>{it.icon || '✦'}</div>
                )}
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="font-bold text-base" placeholder='Title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-2 text-sm opacity-70" placeholder='Description' />
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-xs opacity-50">Tip: For images, use 96 × 96 px PNG/JPG (square) for crisp display.</p>
        </div>
      </section>
    );
  }

  // ── Process Steps · Horizontal (matches `ServicesTimeline` preview) ──
  if (layout === 'process-steps-horizontal') {
    const steps = items.length ? items : [
      { title: 'Discover' }, { title: 'Design' }, { title: 'Build' }, { title: 'Launch' },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          <E block={block} onEdit={onEdit} field='title' value={c.title || 'Our process'} tag='h2' className="text-3xl md:text-4xl font-bold mb-16" style={{ fontFamily: theme.headingFont }} placeholder='Headline' />
          <div className="relative">
            <div className="hidden md:block absolute top-7 left-[8%] right-[8%] h-[2px]" style={{ background: theme.accent + '30' }} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 relative">
              {steps.slice(0,4).map((s,i)=>(
                <div key={i} className="text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center font-bold text-lg relative z-10" style={{ background: i === 0 ? theme.accent : '#fff', color: i === 0 ? '#fff' : theme.accent, border: `2px solid ${theme.accent}` }}>{String(i+1).padStart(2,'0')}</div>
                  <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={s.title} tag='p' className="font-bold text-base" placeholder='Step name' />
                  <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={s.description || ''} tag='p' className="mt-2 text-sm opacity-70 max-w-[14ch] mx-auto leading-snug" placeholder='Short description' />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── Large Feature · Dark (matches `ServicesLargeFeature` preview) ──
  if (layout === 'large-feature-dark') {
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="grid grid-cols-1 md:grid-cols-2" style={{ background: '#0E0E23', color: '#fff' }}>
        <div className="relative min-h-[400px] md:min-h-[500px]">
          {c.image_url
            ? <SafeImg src={c.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
            : <div className="absolute inset-0 bg-gradient-to-br from-violet-600 to-pink-500" />}
        </div>
        <div className="p-12 md:p-16 flex flex-col justify-center">
          <E block={block} onEdit={onEdit} field='eyebrow' value={c.eyebrow || 'Featured'} tag='p' className="text-xs font-extrabold uppercase tracking-[0.2em]" style={{ color: '#F97316' }} placeholder='Featured' />
          <E block={block} onEdit={onEdit} field='title' value={c.title || 'Full Service Brand System'} tag='h2' className="mt-4 text-4xl md:text-5xl font-extrabold leading-[1.05]" style={{ fontFamily: theme.headingFont }} placeholder='Headline' />
          <div className="mt-8 space-y-3">
            {(items.length ? items : [
              { title: 'Strategy' }, { title: 'Identity' }, { title: 'Rollout' },
            ]).slice(0,4).map((it,i)=>(
              <div key={i}>
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="font-bold text-sm" placeholder='Item title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="text-xs opacity-70" placeholder='Item description' />
              </div>
            ))}
          </div>
          <a href={c.cta_link || '#'} className="mt-10 inline-flex items-center gap-2 px-6 py-3 rounded-full text-xs font-extrabold uppercase tracking-wider self-start" style={{ background: '#F97316', color: '#fff' }}>
            <E block={block} onEdit={onEdit} field='cta_label' value={c.cta_label || c.tagline || 'From $4,800'} tag='span' placeholder='CTA' />
            <span>→</span>
          </a>
        </div>
      </section>
    );
  }

  // ── Tabbed Services (matches `ServicesTabbed` preview) ──
  if (layout === 'tabbed-services-content') {
    const tabs = items.length ? items : [
      { title: 'Branding' }, { title: 'Design' }, { title: 'Build' }, { title: 'Care' },
    ];
    const activeIdx = 0; // preview-tab; click handler optional in editor
    const active = tabs[activeIdx] || tabs[0];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: theme.bg, color: theme.ink }}>
        <div className="max-w-6xl mx-auto">
          <div className="flex gap-2 border-b" style={{ borderColor: theme.ink + '20' }}>
            {tabs.slice(0,4).map((t,i)=>(
              <div key={i} className={`px-5 py-3 text-sm font-extrabold uppercase tracking-wider cursor-pointer ${i === activeIdx ? '' : 'opacity-50'}`} style={{ borderBottom: i === activeIdx ? `2px solid ${theme.ink}` : '2px solid transparent', marginBottom: -1 }}>
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={t.title} tag='span' placeholder='Tab' />
              </div>
            ))}
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden">
              {(active.image || c.image_url)
                ? <SafeImg src={active.image || c.image_url} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-violet-200 to-fuchsia-300" />}
            </div>
            <div>
              <E block={block} onEdit={onEdit} field={`items.${activeIdx}.heading`} value={active.heading || c.title || 'Brand Identity Systems'} tag='h3' className="text-2xl md:text-3xl font-extrabold" style={{ fontFamily: theme.headingFont }} placeholder='Tab heading' />
              <E block={block} onEdit={onEdit} field={`items.${activeIdx}.description`} value={active.description || c.tagline || 'Comprehensive identity work — logo, type, color, voice and rollout.'} tag='p' className="mt-4 text-base opacity-75 leading-relaxed" placeholder='Tab body copy' />
            </div>
          </div>
        </div>
      </section>
    );
  }

  // ── Large Service Cards 2×2 (matches `ServicesCardsLarge` preview) ──
  if (layout === 'cards-large-2x2') {
    const four = items.length ? items : [
      { title: 'Brand' }, { title: 'Web' }, { title: 'SEO' }, { title: 'Care' },
    ];
    return (
      <section data-block-type={block.type} data-layout={block.layout || "default"} className="px-6 py-20" style={{ background: '#FAF6EE', color: '#0E0E23' }}>
        <div className="max-w-5xl mx-auto">
          <E block={block} onEdit={onEdit} field='title' value={c.title || 'Our offerings'} tag='h2' className="text-3xl md:text-4xl font-extrabold text-center mb-12" style={{ fontFamily: theme.headingFont }} placeholder='Headline' />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {four.slice(0,4).map((it,i)=>(
              <div key={i} className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="text-4xl font-extrabold" style={{ color: theme.accent || '#8B5CF6' }}>{String(i+1).padStart(2,'0')}</div>
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='p' className="mt-3 text-xl font-extrabold" placeholder='Title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-2 text-sm opacity-75 leading-relaxed" placeholder='Description' />
                {(it.image || it.image_url) && (
                  <div className="mt-4 aspect-video rounded-lg overflow-hidden">
                    <SafeImg src={it.image || it.image_url} alt={it.title || ''} className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return null;
}
